import { useMemo, useState } from "react";
import {
    Button,
    Checkbox,
    FormControlLabel,
    FormGroup,
    Popover,
    Tooltip,
    Typography,
    useMediaQuery,
} from "@mui/material";
import ViewColumnOutlinedIcon from "@mui/icons-material/ViewColumnOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import RestartAltOutlinedIcon from "@mui/icons-material/RestartAltOutlined";
import { catalogQuietButtonSx } from "../Products/catalogGridStyles.js";

function columnLabel(col) {
    return col.headerName || col.field || "";
}

function isHideableColumn(col) {
    if (!col?.field) return false;
    if (col.field === "__check__") return false;
    if (col.hideable === false) return false;
    return true;
}

const controlButtonSx = {
    ...catalogQuietButtonSx,
    flexShrink: 0,
    whiteSpace: "nowrap",
    height: 36,
    minHeight: 36,
    maxHeight: 36,
    px: 1.25,
    lineHeight: 1.2,
};

const iconOnlyButtonSx = {
    ...controlButtonSx,
    minWidth: 36,
    width: 36,
    px: 0,
    "& .MuiButton-startIcon": {
        margin: 0,
    },
};

/**
 * Toolbar controls for saved grid layouts: Columns / Save / Reset.
 * Short labels + fixed height so they match neighboring toolbar actions.
 */
export default function GridLayoutControls({ layout, columns = [], sx }) {
    const [anchorEl, setAnchorEl] = useState(null);
    const iconOnly = useMediaQuery("(max-width:1100px)");
    const hideable = useMemo(
        () => (Array.isArray(columns) ? columns.filter(isHideableColumn) : []),
        [columns]
    );

    if (!layout) return null;

    const visibility = layout.columnVisibilityModel || {};

    const toggleField = (field) => {
        const currentlyVisible = visibility[field] !== false;
        layout.onColumnVisibilityModelChange({
            ...visibility,
            [field]: !currentlyVisible,
        });
    };

    const buttonSx = {
        ...(iconOnly ? iconOnlyButtonSx : controlButtonSx),
        ...sx,
    };

    const renderButton = ({ key, label, tooltip, disabled, startIcon, onClick }) => {
        const button = (
            <Button
                key={key}
                size="small"
                variant="outlined"
                disabled={disabled}
                startIcon={startIcon}
                onClick={onClick}
                aria-label={tooltip}
                sx={buttonSx}
            >
                {iconOnly ? null : label}
            </Button>
        );
        return (
            <Tooltip key={key} title={tooltip} enterDelay={400}>
                <span style={{ display: "inline-flex", flexShrink: 0 }}>{button}</span>
            </Tooltip>
        );
    };

    return (
        <div className="grid-layout-controls">
            {renderButton({
                key: "columns",
                label: "Columns",
                tooltip: "Choose visible columns",
                startIcon: <ViewColumnOutlinedIcon fontSize="small" />,
                onClick: (e) => setAnchorEl(e.currentTarget),
            })}
            {renderButton({
                key: "save",
                label: layout.saving ? "Saving…" : "Save",
                tooltip: layout.saving ? "Saving layout…" : "Save layout",
                disabled: !layout.isDirty || layout.saving,
                startIcon: <SaveOutlinedIcon fontSize="small" />,
                onClick: () => layout.save(),
            })}
            {renderButton({
                key: "reset",
                label: "Reset",
                tooltip: "Reset layout to defaults",
                disabled: layout.saving || (!layout.hasSaved && !layout.isDirty),
                startIcon: <RestartAltOutlinedIcon fontSize="small" />,
                onClick: () => layout.reset(),
            })}
            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
                marginThreshold={12}
                slotProps={{
                    paper: {
                        sx: {
                            mt: 0.5,
                            p: 1.5,
                            width: "min(280px, calc(100vw - 24px))",
                            maxWidth: "calc(100vw - 24px)",
                            maxHeight: "min(360px, calc(100vh - 96px))",
                            overflow: "auto",
                            borderRadius: "10px",
                            border: "1px solid #e2e8f0",
                            boxShadow: "0 8px 24px rgba(15, 23, 42, 0.08)",
                        },
                    },
                }}
            >
                <Typography
                    variant="caption"
                    sx={{
                        display: "block",
                        mb: 1,
                        fontWeight: 700,
                        letterSpacing: "0.04em",
                        textTransform: "uppercase",
                        color: "#5c6570",
                    }}
                >
                    Visible columns
                </Typography>
                <FormGroup>
                    {hideable.map((col) => (
                        <FormControlLabel
                            key={col.field}
                            control={
                                <Checkbox
                                    size="small"
                                    checked={visibility[col.field] !== false}
                                    onChange={() => toggleField(col.field)}
                                    sx={{ p: iconOnly ? 1.25 : 0.75 }}
                                />
                            }
                            label={columnLabel(col)}
                            sx={{
                                m: 0,
                                ml: -0.5,
                                py: iconOnly ? 0.25 : 0,
                                minHeight: iconOnly ? 40 : undefined,
                                "& .MuiFormControlLabel-label": {
                                    fontSize: 13.5,
                                    color: "#1c2430",
                                    lineHeight: 1.3,
                                    wordBreak: "break-word",
                                },
                            }}
                        />
                    ))}
                </FormGroup>
            </Popover>
        </div>
    );
}
